import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
  Button,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Badge,
  Switch,
  FormControlLabel,
  Slider,
  Stack,
  Rating,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  LocationOn as LocationIcon,
  AttachMoney as MoneyIcon,
  Home as HomeIcon,
  School as SchoolIcon,
  DirectionsWalk as WalkIcon,
  DirectionsCar as CarIcon,
  DirectionsBike as BikeIcon,
  Security as SecurityIcon,
  Refresh as RefreshIcon,
  Assessment as AssessmentIcon,
  Compare as CompareIcon,
  Timeline as TimelineIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Analytics as AnalyticsIcon,
  ExpandMore as ExpandMoreIcon,
  Settings as SettingsIcon,
  FilterList as FilterIcon,
  Search as SearchIcon,
  Today as TodayIcon,
  Event as EventIcon,
  Block as BlockIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  People as PeopleIcon,
  Speed as SpeedIcon,
  PersonAdd as LeadIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import AdvancedMarketAnalysis from './AdvancedMarketAnalysis';
import LeadGenerationSystem from './LeadGenerationSystem';

function TabPanel({
  children, value, index, ...other
}) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`market-tabpanel-${index}`}
      aria-labelledby={`market-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function MarketAnalysis({ property, onRefresh }) {
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [marketData, setMarketData] = useState(null);

  // Mock market data - in real implementation, this would come from APIs
  const mockMarketData = {
    comparableSales: [
      {
        id: 1,
        address: '125 Main St, San Francisco, CA',
        price: 1150000,
        soldDate: '2024-05-15',
        sqft: 1200,
        bedrooms: 2,
        bathrooms: 2,
        pricePerSqFt: 958,
        daysOnMarket: 45,
      },
      {
        id: 2,
        address: '127 Main St, San Francisco, CA',
        price: 1250000,
        soldDate: '2024-04-20',
        sqft: 1300,
        bedrooms: 2,
        bathrooms: 2.5,
        pricePerSqFt: 962,
        daysOnMarket: 32,
      },
      {
        id: 3,
        address: '129 Main St, San Francisco, CA',
        price: 1180000,
        soldDate: '2024-03-10',
        sqft: 1250,
        bedrooms: 2,
        bathrooms: 2,
        pricePerSqFt: 944,
        daysOnMarket: 28,
      },
    ],
    marketTrends: {
      averagePrice: 1193333,
      averageDaysOnMarket: 35,
      pricePerSqFt: 955,
      priceChange: 2.5, // percentage
      inventoryChange: -5.2, // percentage
    },
    neighborhood: {
      walkScore: 85,
      transitScore: 78,
      bikeScore: 72,
      schoolRating: 8.5,
      crimeRate: 'Low',
      amenities: ['Restaurants', 'Shopping', 'Parks', 'Schools'],
    },
  };

  useEffect(() => {
    if (property) {
      setMarketData(mockMarketData);
    }
  }, [property]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const formatPrice = (price) => (price ? `$${price.toLocaleString()}` : 'N/A');

  const formatDate = (dateString) => new Date(dateString).toLocaleDateString();

  const getScoreColor = (score) => {
    if (score >= 80) return 'success';
    if (score >= 60) return 'warning';
    return 'error';
  };

  const getTrendIcon = (change) => {
    if (change > 0) return <TrendingUpIcon color="success" />;
    if (change < 0) return <TrendingDownIcon color="error" />;
    return null;
  };

  if (!property) {
    return (
      <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
        <Typography variant="h6">Select a property to view market analysis.</Typography>
      </Box>
    );
  }

  if (!marketData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper elevation={2} sx={{ p: 3 }}>
      <Box sx={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3,
      }}
      >
        <Typography variant="h5" fontWeight={700}>
          Market Analysis & Lead Generation
        </Typography>
        <Tooltip title="Refresh market data">
          <IconButton onClick={onRefresh} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 2 }}>
        <Tab label="Basic Analysis" icon={<AnalyticsIcon />} />
        <Tab label="Advanced Analysis" icon={<AssessmentIcon />} />
        <Tab label="Lead Generation" icon={<LeadIcon />} />
      </Tabs>

      <TabPanel value={activeTab} index={0}>
        <Box>
          <Typography variant="h6" gutterBottom>
            Market Overview
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Market Trends
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: 'success.main' }}>
                          <TrendingUpIcon />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary="Average Sale Price"
                        secondary="$1,225,000 (+2.5% from last month)"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: 'info.main' }}>
                          <SpeedIcon />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary="Days on Market"
                        secondary="42 days (-5% from last month)"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: 'warning.main' }}>
                          <BusinessIcon />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary="Inventory"
                        secondary="45 properties (3.2 months supply)"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: 'primary.main' }}>
                          <StarIcon />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary="Price per Sq Ft"
                        secondary="$490 (+1.8% from last month)"
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Neighborhood Insights
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: 'success.main' }}>
                          <LocationIcon />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary="Walk Score"
                        secondary="85/100 (Very Walkable)"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: 'info.main' }}>
                          <PeopleIcon />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary="School Rating"
                        secondary="8.5/10 (Excellent Schools)"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: 'warning.main' }}>
                          <MoneyIcon />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary="Median Income"
                        secondary="$85,000 (Above Average)"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: 'primary.main' }}>
                          <HomeIcon />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary="Home Ownership"
                        secondary="65% (Stable Community)"
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Comparable Sales
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Address</TableCell>
                      <TableCell>Price</TableCell>
                      <TableCell>Sq Ft</TableCell>
                      <TableCell>Beds/Baths</TableCell>
                      <TableCell>Days on Market</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell>125 Main St</TableCell>
                      <TableCell>$1,180,000</TableCell>
                      <TableCell>2,400</TableCell>
                      <TableCell>3/2</TableCell>
                      <TableCell>38</TableCell>
                      <TableCell>
                        <Chip label="Sold" color="success" size="small" />
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>130 Oak Ave</TableCell>
                      <TableCell>$1,320,000</TableCell>
                      <TableCell>2,600</TableCell>
                      <TableCell>4/3</TableCell>
                      <TableCell>42</TableCell>
                      <TableCell>
                        <Chip label="Sold" color="success" size="small" />
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>115 Pine St</TableCell>
                      <TableCell>$1,100,000</TableCell>
                      <TableCell>2,300</TableCell>
                      <TableCell>3/2</TableCell>
                      <TableCell>15</TableCell>
                      <TableCell>
                        <Chip label="Active" color="primary" size="small" />
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Box>
      </TabPanel>

      <TabPanel value={activeTab} index={1}>
        <AdvancedMarketAnalysis property={property} onUpdate={onRefresh} />
      </TabPanel>

      <TabPanel value={activeTab} index={2}>
        <LeadGenerationSystem property={property} onUpdate={onRefresh} />
      </TabPanel>
    </Paper>
  );
}

export default MarketAnalysis;
